import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../api/client.js';

export default function ManagerDashboard() {
  const d = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => api.get('/admin/dashboard').then(r => r.data.data),
  });

  if (d.isLoading) return <div className="text-muted">Loading…</div>;
  if (!d.data) return <div className="text-crimson">Failed to load.</div>;
  const x = d.data;

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Manager</div>
        <h1 className="text-2xl">Dashboard</h1>
        <div className="rule-crimson" />
      </header>

      {/* Pipeline forecast strip */}
      {x.pipeline && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Pill label="Open pipeline" value={fmtMoney(x.pipeline.total_open)} />
          <Pill label="Weighted forecast" value={fmtMoney(x.pipeline.weighted)} highlight />
          <Pill label="Open deals" value={x.pipeline.open_count} />
          <Pill label="Closing 30d" value={`${x.pipeline.closing_30d_count} · ${fmtMoney(x.pipeline.closing_30d_value)}`} />
          <Pill label="Avg days to win (90d)"
                value={x.pipeline_velocity?.avg_days_to_win != null ? `${x.pipeline_velocity.avg_days_to_win}d` : '—'} />
        </div>
      )}

      {/* Pipeline funnel by stage */}
      {x.pipeline_by_stage && x.pipeline_by_stage.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Pipeline funnel</div>
          <div className="rule-crimson" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {x.pipeline_by_stage.map(s => (
              <div key={s.stage} className="rounded bg-panel p-2 text-center">
                <div className="text-lg font-bold text-navy">{s.n}</div>
                <div className="text-xs text-muted capitalize">{s.stage}</div>
                <div className="text-xs text-navy">{fmtMoney(s.value)}</div>
                {s.avg_age_days != null && (
                  <div className="text-[10px] text-muted mt-0.5">{s.avg_age_days}d avg age</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Per-rep pipeline rollup */}
      {x.pipeline_by_rep && x.pipeline_by_rep.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Pipeline by rep</div>
          <div className="rule-crimson" />
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted">
                <tr>
                  <th className="py-1">Rep</th>
                  <th className="text-right">Open</th>
                  <th className="text-right">Open value</th>
                  <th className="text-right">Weighted</th>
                  <th className="text-right">Won 90d</th>
                  <th className="text-right">Lost 90d</th>
                  <th className="text-right">Win rate</th>
                </tr>
              </thead>
              <tbody>
                {x.pipeline_by_rep.map(r => {
                  const closed = r.won_90d + r.lost_90d;
                  const winRate = closed > 0 ? Math.round((r.won_90d / closed) * 100) : null;
                  return (
                    <tr key={r.user_id} className="border-t border-panelDark">
                      <td className="py-1 font-medium">
                        <Link to={`/admin/reps/${r.user_id}`} className="text-navy hover:text-crimson">{r.full_name}</Link>
                      </td>
                      <td className="text-right">{r.open_count}</td>
                      <td className="text-right">{fmtMoney(r.open_value)}</td>
                      <td className="text-right text-crimson font-semibold">{fmtMoney(r.weighted)}</td>
                      <td className="text-right">{r.won_90d} · {fmtMoney(r.won_value_90d)}</td>
                      <td className="text-right text-muted">{r.lost_90d}</td>
                      <td className="text-right">{winRate != null ? `${winRate}%` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Aging deals */}
      {x.aging_deals && x.aging_deals.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Aging deals (open &gt; 60 days)</div>
          <div className="rule-crimson" />
          <div className="space-y-1 mt-2 text-sm">
            {x.aging_deals.map(d => (
              <div key={d.id} className="flex items-baseline justify-between bg-panel rounded p-2">
                <div className="flex-1">
                  <span className="font-medium">{d.name}</span>
                  <span className="text-muted text-xs ml-2">
                    {d.customer_name || '(no customer)'} · {d.user_name} · {d.stage}
                  </span>
                </div>
                <div className="text-xs text-crimson font-semibold whitespace-nowrap ml-2">
                  {d.age_days}d · {fmtMoney(d.value_usd)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Per-rep activity this week */}
      <section className="card p-4">
        <div className="section-eyebrow">Rep activity (this week)</div>
        <div className="rule-crimson" />
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted">
              <tr>
                <th className="py-1">Rep</th>
                <th>Branch</th>
                <th className="text-right">Notes</th>
                <th className="text-right">Visits</th>
                <th className="text-right">Calls</th>
                <th className="text-right">Emails</th>
                <th className="text-right">Quotes</th>
                <th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {x.reps.map(r => (
                <RepActivityRow key={r.id} r={r} />
              ))}
              {x.reps.length === 0 && (
                <tr><td colSpan="8" className="text-muted py-3 text-center">No reps in scope.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Open follow-ups */}
        <section className="card p-4">
          <div className="section-eyebrow">Open follow-ups</div>
          <div className="rule-crimson" />
          {x.followups.length === 0 && <div className="text-muted text-sm mt-2">None.</div>}
          <div className="space-y-1 mt-2 text-sm">
            {x.followups.map(f => (
              <div key={f.urgency} className="flex justify-between">
                <span className="capitalize">{f.urgency}</span>
                <span><b>{f.n}</b>{f.overdue > 0 && <span className="text-crimson"> · {f.overdue} overdue</span>}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Recap stats */}
        <section className="card p-4">
          <div className="section-eyebrow">Recaps (last 5 weeks)</div>
          <div className="rule-crimson" />
          {x.recap_stats.length === 0 && <div className="text-muted text-sm mt-2">No recaps yet.</div>}
          <div className="space-y-1 mt-2 text-sm">
            {Object.entries(groupByWeek(x.recap_stats)).map(([week, statuses]) => (
              <div key={week} className="flex justify-between">
                <span>{week}</span>
                <span className="text-muted">
                  {(statuses.submitted || 0)} submitted · {(statuses.draft || 0)} draft
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Recent alerts */}
      <section className="card p-4">
        <div className="section-eyebrow">⚠️ Recent high alerts (14d)</div>
        <div className="rule-crimson" />
        {x.alert_notes.length === 0 && <div className="text-muted text-sm mt-2">No alerts. Nice.</div>}
        <div className="space-y-2 mt-2">
          {x.alert_notes.map(n => (
            <Link key={n.id} to={`/notes/${n.id}`} className="block bg-panel rounded p-2 text-sm hover:ring-1 hover:ring-crimson">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium">
                  {n.customer_name || '(no customer)'} <span className="text-muted">· {n.user_name}</span>
                </div>
                <div className="text-xs text-muted">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {n.summary && <div className="text-muted text-xs mt-1">{n.summary}</div>}
            </Link>
          ))}
        </div>
      </section>

      {/* Stale customers */}
      <section className="card p-4">
        <div className="section-eyebrow">Customers with no touch in 30+ days ({x.stale_customers.length})</div>
        <div className="rule-crimson" />
        {x.stale_customers.length === 0 && <div className="text-muted text-sm mt-2">All caught up.</div>}
        <div className="space-y-1 mt-2 text-sm">
          {x.stale_customers.slice(0, 25).map(c => (
            <Link key={c.id} to={`/customers/${c.id}`} className="block bg-panel rounded p-2 hover:ring-1 hover:ring-crimson flex items-baseline justify-between">
              <div>
                <span className="font-medium">{c.name}</span>
                <span className="text-muted text-xs ml-2">{[c.city, c.state].filter(Boolean).join(', ')}</span>
              </div>
              <div className={`text-xs ${c.days_since > 90 ? 'text-crimson font-semibold' : 'text-muted'}`}>
                {c.days_since}d
              </div>
            </Link>
          ))}
          {x.stale_customers.length > 25 && (
            <div className="text-muted text-xs text-center pt-2">+ {x.stale_customers.length - 25} more</div>
          )}
        </div>
      </section>
    </div>
  );
}

// Rep activity row with a click-to-expand drop-down that lazily loads and
// shows the rep's associated data (CRM Task List #9). The full rep detail
// page remains reachable from inside the expanded panel.
function RepActivityRow({ r }) {
  const [open, setOpen] = useState(false);
  const detail = useQuery({
    queryKey: ['admin-rep', String(r.id)],
    queryFn: () => api.get(`/admin/reps/${r.id}`).then(res => res.data.data),
    enabled: open,
    staleTime: 60_000,
  });

  return (
    <>
      <tr className="border-t border-panelDark">
        <td className="py-1 font-medium">
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className="text-navy hover:text-crimson text-left inline-flex items-center gap-1"
          >
            <span className={`text-[10px] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
            {r.full_name}
          </button>
        </td>
        <td className="text-muted">{r.branch_name || '—'}</td>
        <td className="text-right">{r.notes_week}</td>
        <td className="text-right">{r.visits_week}</td>
        <td className="text-right">{r.calls_week}</td>
        <td className="text-right">{r.emails_week}</td>
        <td className="text-right">{r.quotes_week}</td>
        <td className="text-muted text-xs">
          {r.last_note_at ? new Date(r.last_note_at).toLocaleDateString() : '—'}
        </td>
      </tr>
      {open && (
        <tr className="border-t border-panelDark bg-panel/40">
          <td colSpan="8" className="p-3">
            {detail.isLoading && (
              <div className="text-muted text-xs">Loading {r.full_name}'s details…</div>
            )}
            {detail.isError && (
              <div className="text-crimson text-xs">
                Failed to load rep details — {detail.error?.response?.data?.error || detail.error?.message || 'unknown error'}
                <button
                  type="button"
                  onClick={() => detail.refetch()}
                  className="ml-2 underline hover:text-navy"
                >
                  retry
                </button>
              </div>
            )}
            {!detail.isLoading && !detail.isError && detail.data && (
              <RepDetailSummary d={detail.data} repId={r.id} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function RepDetailSummary({ d, repId }) {
  const k = d.kpis;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MiniStat
          label="Pipeline (weighted)"
          value={fmtMoney(k.pipeline.weighted)}
          sub={`${k.pipeline.open_count} open · ${fmtMoney(k.pipeline.open_value)}`}
        />
        <MiniStat
          label="Follow-ups"
          value={k.followups.open_count}
          sub={k.followups.overdue > 0 ? `${k.followups.overdue} overdue` : `${k.followups.due_this_week} due this week`}
        />
        <MiniStat
          label="This week"
          value={k.activity_week.notes}
          sub={`${k.activity_week.visits}v · ${k.activity_week.calls}c · ${k.activity_week.emails}e`}
        />
        <MiniStat
          label="Coverage"
          value={k.coverage.total_owned > 0
            ? `${Math.round((k.coverage.touched_30d / k.coverage.total_owned) * 100)}%`
            : '—'}
          sub={`${k.coverage.touched_30d} of ${k.coverage.total_owned} in 30d`}
        />
      </div>

      {d.open_deals && d.open_deals.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted mb-1">Open deals</div>
          <div className="space-y-1">
            {d.open_deals.slice(0, 5).map(o => (
              <div key={o.id} className="text-xs flex items-baseline justify-between gap-2">
                <span className="truncate">
                  <span className="font-medium">{o.name}</span>
                  {o.customer_name && <span className="text-muted"> · {o.customer_name}</span>}
                </span>
                <span className="tabular-nums whitespace-nowrap">{fmtMoney(o.value_usd)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Link to={`/admin/reps/${repId}`} className="inline-block text-xs text-crimson hover:underline">
        View full rep detail →
      </Link>
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="rounded bg-white/60 p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
      <div className="text-base font-bold text-navy">{value}</div>
      <div className="text-[11px] text-muted">{sub}</div>
    </div>
  );
}

function Pill({ label, value, highlight }) {
  return (
    <div className={`card p-3 text-center ${highlight ? 'ring-1 ring-crimson' : ''}`}>
      <div className={`text-lg font-bold ${highlight ? 'text-crimson' : 'text-navy'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}

function fmtMoney(v) {
  const n = Number(v || 0);
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return '$' + Math.round(n / 1000) + 'k';
  return '$' + Math.round(n);
}

function groupByWeek(rows) {
  const out = {};
  for (const r of rows) {
    const wk = String(r.week_start_date).slice(0, 10);
    out[wk] = out[wk] || {};
    out[wk][r.status] = (out[wk][r.status] || 0) + Number(r.n);
  }
  return out;
}
